import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { useRouterState } from '@tanstack/react-router';

import { cn } from '../../lib/utils';
import { isPathActive, useActivePath } from '../../hooks/use-active-path';
import type { NavigationItem } from '../../types/navigation';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '../ui/sidebar';

export interface SidebarNavProps {
  /** CSS classes applied to the outer `<SidebarGroup>`. */
  className?: string;
  /** Optional function to check if the user has a specific permission.
   *  When provided, items with a `requiredPermission` are only rendered
   *  if this function returns true for that permission.
   *  When not provided, all items are rendered (backward compatible). */
  hasPermission?: (permission: string) => boolean;
  /** Navigation items to render. */
  items: NavigationItem[];
  /**
   * Render function that produces a routable link for each item.
   * The component is router-agnostic — the consumer provides the link implementation
   * (e.g. TanStack `<Link>`, React Router `<Link>`, or a plain `<a>`).
   */
  renderLink: (props: { children: React.ReactNode; path: string }) => React.ReactNode;
  /**
   * Translation function — maps an i18n key to the localized string. Typically `t` from `useTranslation()`.
   */
  translate: (key: string) => string;
}

// ─── Numeric badge ────────────────────────────────────────────────────────────

/** The largest count shown as is; anything above reads "99+" so a long number never widens the sidebar. */
const MAX_BADGE_COUNT = 99;

/** True when the item carries a numeric badge worth showing: a count greater than 0. */
function hasCountBadge(item: NavigationItem): item is NavigationItem & { badgeCount: number } {
  return item.badgeCount !== undefined && item.badgeCount > 0;
}

/**
 * The accessible name of a link (or inert button) that carries a numeric badge: its label, any text badge and the
 * count's pre-translated label (`badgeCountLabel`, else the bare number), e.g. "Reminders, 3 overdue". It names the whole item rather than the pill, because
 * the pill is no live region (a background refetch must not be announced) and the collapsed icon rail hides both the
 * label and the pill, so the name is the only place assistive tech can read the count from. Undefined without a count,
 * leaving the name to come from the item's content as before.
 */
function countAccessibleName(item: NavigationItem, label: string, badge: string | undefined): string | undefined {
  if (!hasCountBadge(item)) return undefined;
  const countLabel = item.badgeCountLabel ?? String(item.badgeCount);
  return [label, badge, countLabel].filter((part) => part !== undefined).join(', ');
}

/**
 * The numeric badge of an item (e.g. the Overdue reminders count), or nothing when the count is absent or 0.
 * Unlike the text badge it is a red pill. It is purely visual (`aria-hidden`): the count reaches assistive tech through
 * the item's accessible name (see `countAccessibleName`).
 */
function CountBadge({
  item,
  withRailDot = false,
}: {
  item: NavigationItem;
  /** True on a top-level item, the only kind the collapsed icon rail shows. */
  withRailDot?: boolean;
}): React.JSX.Element | null {
  if (!hasCountBadge(item)) return null;
  const { badgeCount } = item;

  return (
    <>
      <span
        aria-hidden="true"
        className="min-w-5 rounded-full bg-destructive px-1.5 py-0.5 text-center text-[11px] font-semibold leading-none text-destructive-foreground"
        data-testid="nav-count-badge"
      >
        {badgeCount > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : badgeCount}
      </span>
      {/* The collapsed icon rail hides every span of the button, so a dot on the icon stands in for the count. */}
      {withRailDot && (
        <div
          aria-hidden="true"
          className="absolute end-1.5 top-1.5 hidden size-2.5 rounded-full bg-destructive ring-2 ring-sidebar group-data-[collapsible=icon]:block"
          data-testid="nav-count-badge-dot"
        />
      )}
    </>
  );
}

// ─── Flat item (no children) ─────────────────────────────────────────────────

/**
 * Single flat navigation item — extracted so each item can call `useActivePath`
 * independently (hooks must be called at the top level of a component).
 *
 * When `item.disabled` is true the item renders inert: no link/anchor, no active
 * state, `aria-disabled="true"`, and a `cursor-not-allowed` style. The badge and
 * tooltip are still rendered so the user can see why the item is disabled (SOON).
 */
function SidebarNavItem({
  item,
  renderLink,
  translate,
}: {
  item: NavigationItem;
  renderLink: SidebarNavProps['renderLink'];
  translate: SidebarNavProps['translate'];
}): React.JSX.Element {
  const isActiveFromPath = useActivePath(item.path);
  // Disabled items are never shown as active — skip the active-path result.
  const isActive = item.disabled ? false : isActiveFromPath;
  const Icon = item.icon;
  const label = translate(item.labelKey);
  const badge = item.badgeLabelKey ? translate(item.badgeLabelKey) : undefined;
  const accessibleName = countAccessibleName(item, label, badge);

  const content = (
    <>
      <Icon className={cn('size-5', isActive && 'text-sidebar-primary')} />
      <span className="flex-1 font-medium">{label}</span>
      {badge !== undefined && (
        <span className="rounded bg-sidebar-primary px-1 py-0.5 text-[10px] font-semibold uppercase leading-none text-sidebar-primary-foreground">
          {badge}
        </span>
      )}
      <CountBadge item={item} withRailDot />
    </>
  );

  if (item.disabled) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          aria-disabled="true"
          aria-label={accessibleName}
          className="relative cursor-not-allowed gap-3 px-3 py-2.5 text-base text-sidebar-foreground/40"
          isActive={false}
          tooltip={label}
        >
          {content}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        aria-label={accessibleName}
        className="relative gap-3 px-3 py-2.5 text-base text-sidebar-foreground/70"
        isActive={isActive}
        tooltip={label}
      >
        {renderLink({
          children: content,
          path: item.path,
        })}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

// ─── Collapsible group sub-item ───────────────────────────────────────────────

/**
 * A single sub-item rendered inside a `SidebarMenuSub` list.
 *
 * Active state is resolved by the parent group (most-specific match wins) and
 * passed in via `isActive` — sub-items don't call `useActivePath` themselves, so
 * nested sibling routes (e.g. `/vehicles` vs `/vehicles/add`) resolve to exactly
 * one active item.
 *
 * When `item.disabled` is true the sub-item renders inert: no link/anchor,
 * `aria-disabled="true"`, muted text + `cursor-not-allowed`, never active. The
 * badge (`item.badgeLabelKey`) is still rendered using the same orange badge
 * styling as `SidebarNavItem`.
 */
function SidebarNavSubItem({
  isActive,
  item,
  renderLink,
  translate,
}: {
  isActive: boolean;
  item: NavigationItem;
  renderLink: SidebarNavProps['renderLink'];
  translate: SidebarNavProps['translate'];
}): React.JSX.Element {
  const Icon = item.icon;
  const label = translate(item.labelKey);
  const badge = item.badgeLabelKey ? translate(item.badgeLabelKey) : undefined;
  const accessibleName = countAccessibleName(item, label, badge);

  if (item.disabled) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton
          aria-disabled="true"
          aria-label={accessibleName}
          className="h-auto cursor-not-allowed gap-2 py-2 pe-3 text-[15px] text-sidebar-foreground/40"
          isActive={false}
          size="md"
        >
          <Icon className="size-4" />
          {/* flex-1 pushes the badge to the trailing edge, as on top-level items. */}
          <span className="flex-1 font-medium">{label}</span>
          {badge !== undefined && (
            <span className="rounded bg-sidebar-primary px-1 py-0.5 text-[10px] font-semibold uppercase leading-none text-sidebar-primary-foreground">
              {badge}
            </span>
          )}
          <CountBadge item={item} />
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton
        asChild
        aria-label={accessibleName}
        className="h-auto gap-2 py-2 pe-3 text-[15px]"
        isActive={isActive}
        size="md"
      >
        {renderLink({
          children: (
            <>
              <Icon className={cn('size-4', isActive && 'text-sidebar-primary')} />
              <span className={cn('flex-1 font-medium', isActive && 'text-sidebar-accent-foreground')}>{label}</span>
              <CountBadge item={item} />
            </>
          ),
          path: item.path,
        })}
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}

// ─── Flyout sub-item (collapsed rail) ────────────────────────────────────────

/**
 * A single child rendered inside the collapsed-rail flyout `DropdownMenu`.
 * Mirrors `SidebarNavSubItem` (parent-resolved `isActive`, disabled handling,
 * badge) but renders a `DropdownMenuItem` instead of a sidebar sub-button, so
 * children remain reachable when the sidebar is collapsed to the icon rail.
 */
function SidebarNavFlyoutItem({
  isActive,
  item,
  renderLink,
  translate,
}: {
  isActive: boolean;
  item: NavigationItem;
  renderLink: SidebarNavProps['renderLink'];
  translate: SidebarNavProps['translate'];
}): React.JSX.Element {
  const Icon = item.icon;
  const label = translate(item.labelKey);
  const badge = item.badgeLabelKey ? translate(item.badgeLabelKey) : undefined;
  const accessibleName = countAccessibleName(item, label, badge);

  const content = (
    <>
      <Icon className={cn('size-4', isActive && 'text-primary')} />
      <span className="flex-1 font-medium">{label}</span>
      {badge !== undefined && (
        <span className="rounded bg-sidebar-primary px-1 py-0.5 text-[10px] font-semibold uppercase leading-none text-sidebar-primary-foreground">
          {badge}
        </span>
      )}
      <CountBadge item={item} />
    </>
  );

  if (item.disabled) {
    return (
      <DropdownMenuItem aria-disabled="true" aria-label={accessibleName} className="gap-3" disabled>
        {content}
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem
      asChild
      aria-label={accessibleName}
      className={cn('cursor-pointer gap-3', isActive && 'bg-accent text-accent-foreground')}
      data-active={isActive}
    >
      {renderLink({ children: content, path: item.path })}
    </DropdownMenuItem>
  );
}

// ─── Active child resolution ─────────────────────────────────────────────────

/**
 * Reads the current pathname once and returns the path of the **most specific**
 * active child — the longest child path that matches the route (see
 * `isPathActive`) — or `null` when none match.
 *
 * "Longest match wins" is what disambiguates nested sibling routes: on
 * `/vehicles/add` both `/vehicles` (List) and `/vehicles/add` (Add) match, but
 * the longer `/vehicles/add` wins, so only Add reads active; on a vehicle detail
 * route like `/vehicles/42` only `/vehicles` matches, so List stays active.
 *
 * This replaces the previous fixed-count `useActivePath` fan-out (which padded
 * the list to 10 with `''` and so capped groups at 10 children — and lit every
 * group, because `''` is a prefix of every pathname): a single subscription, no
 * per-child hooks, no child cap, no empty-string false positives.
 *
 * Gotcha A — auto-expand on deep link: the Collapsible mounts with
 * `defaultOpen=true` when this returns non-null (i.e. a child route is active).
 */
function useActiveChildPath(paths: string[]): string | null {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  let activePath: string | null = null;
  for (const path of paths) {
    if (isPathActive(pathname, path) && (activePath === null || path.length > activePath.length)) {
      activePath = path;
    }
  }
  return activePath;
}

// ─── Collapsible group ────────────────────────────────────────────────────────

/**
 * Renders a collapsible group for items that have `children`.
 *
 * Active state: the single active child is resolved once via `useActiveChildPath`
 * (longest match wins) and passed down to each child as `isActive`, so nested
 * sibling routes light exactly one item. The group header / rail icon reads
 * active whenever any child is active.
 *
 * Gotcha A — auto-expand on deep link: `defaultOpen` is `true` when a visible
 * child's path is active (e.g. navigating directly to a child route).
 *
 * Gotcha B — empty group: when all children are permission-filtered, renders nothing.
 *
 * Gotcha C — collapsed rail: when the sidebar is collapsed to the icon rail
 * (desktop only), the inline collapsible can't expand (its `SidebarMenuSub` is
 * hidden in icon mode) and the chevron leaks into the rail. So we render a
 * portal'd `DropdownMenu` flyout instead — the icon stays in the rail and the
 * children open as a popover next to it. On mobile the sidebar is a full
 * off-canvas sheet (never the icon rail), so the inline collapsible is used.
 */
function SidebarNavGroup({
  hasPermission,
  item,
  renderLink,
  translate,
}: {
  hasPermission?: SidebarNavProps['hasPermission'];
  item: NavigationItem;
  renderLink: SidebarNavProps['renderLink'];
  translate: SidebarNavProps['translate'];
}): React.JSX.Element | null {
  const { isMobile, state } = useSidebar();
  const children = item.children ?? [];

  const visibleChildren = hasPermission
    ? children.filter((child) => !child.requiredPermission || hasPermission(child.requiredPermission))
    : children;

  // Active child resolution (Gotcha A): the most-specific matching child wins.
  // Disabled children are excluded — they are never navigable, so never active.
  const activeChildPath = useActiveChildPath(visibleChildren.filter((c) => !c.disabled).map((c) => c.path));
  const anyChildActive = activeChildPath !== null;

  // Gotcha B: nothing to show — render nothing (no empty header/collapsible).
  if (visibleChildren.length === 0) return null;

  const Icon = item.icon;
  const label = translate(item.labelKey);

  // Gotcha C: collapsed icon rail (desktop) → flyout dropdown instead of inline collapsible.
  if (state === 'collapsed' && !isMobile) {
    return (
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              className="cursor-pointer gap-3 px-3 py-2.5 text-base text-sidebar-foreground/70"
              isActive={anyChildActive}
            >
              <Icon className="size-5" />
              <span className="flex-1 font-medium">{label}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-48" side="right" sideOffset={8}>
            <DropdownMenuLabel>{label}</DropdownMenuLabel>
            {visibleChildren.map((child) => (
              <SidebarNavFlyoutItem
                key={child.path}
                isActive={!child.disabled && child.path === activeChildPath}
                item={child}
                renderLink={renderLink}
                translate={translate}
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <Collapsible className="group/collapsible" defaultOpen={anyChildActive}>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            className="cursor-pointer gap-3 px-3 py-2.5 text-base text-sidebar-foreground/70"
            tooltip={label}
          >
            <Icon className="size-5" />
            <span className="flex-1 font-medium">{label}</span>
            <ChevronDown className="ms-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {/* No trailing margin/padding on the sub-list, and pe-3 on each sub-item below (as px-3 on top-level
              items), so child badges and highlights end on the same edge as top-level ones. Sub-items use a
              tighter gap-2 so an icon, a label like "Leased vehicles" and a SOON badge fit on one line. */}
          <SidebarMenuSub className="me-0 gap-1.5 pe-0 py-1.5">
            {visibleChildren.map((child) => (
              <SidebarNavSubItem
                key={child.path}
                isActive={!child.disabled && child.path === activeChildPath}
                item={child}
                renderLink={renderLink}
                translate={translate}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenuItem>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function SidebarNav({
  className,
  hasPermission,
  items,
  renderLink,
  translate,
}: SidebarNavProps): React.JSX.Element {
  const visibleItems = hasPermission
    ? items.filter((item) => !item.requiredPermission || hasPermission(item.requiredPermission))
    : items;

  return (
    <SidebarGroup className={cn('space-y-1 p-0', className)}>
      <SidebarGroupContent>
        <SidebarMenu>
          {visibleItems.map((item) =>
            item.children !== undefined ? (
              <SidebarNavGroup
                key={item.path}
                hasPermission={hasPermission}
                item={item}
                renderLink={renderLink}
                translate={translate}
              />
            ) : (
              <SidebarNavItem key={item.path} item={item} renderLink={renderLink} translate={translate} />
            ),
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
